"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Globe, Music } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="7733" className="hidden lg:flex items-center gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-foreground/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-foreground/10 animate-pulse rounded" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-lg shadow-primary/5" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/image_lierugz9.png" alt="MollyCall.ai" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:block">
          <MenuProvider menu_id="16" className="w-full">
            <DesktopNavConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-6">
          {}
          <Select defaultValue="en">
            <SelectTrigger className="w-[70px] border-none bg-transparent text-foreground focus:ring-0 focus:ring-offset-0 shadow-none px-0 gap-1 hover:text-primary transition-colors">
              <SelectValue placeholder="EN" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border text-foreground min-w-[80px]">
              <SelectItem value="en">EN</SelectItem>
              <SelectItem value="es">ES</SelectItem>
              <SelectItem value="fr">FR</SelectItem>
            </SelectContent>
          </Select>

          {}
          <Link to="/ContactUsPage">
            <Button className="rounded-[10px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(37,99,235,0.3)] font-semibold px-6">
              Request Demo
            </Button>
          </Link>
        </div>

        {}
        <div className="lg:hidden flex items-center gap-4">
           <Select defaultValue="en">
            <SelectTrigger className="w-[60px] border-none bg-transparent text-foreground focus:ring-0 focus:ring-offset-0 shadow-none px-0 gap-1">
              <SelectValue placeholder="EN" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border text-foreground min-w-[80px]">
              <SelectItem value="en">EN</SelectItem>
              <SelectItem value="es">ES</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="ghost" size="icon" className="text-foreground hover:bg-foreground/10" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background flex flex-col lg:hidden animate-in slide-in-from-right duration-300">
          <div className="flex items-center justify-between p-4 border-b border-border h-20">
             <Link to="/HomePage" className="flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/image_lierugz9.png" alt="MollyCall.ai" className="h-7 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" className="text-foreground hover:bg-foreground/10" onClick={() => setIsMobileMenuOpen(false)}>
              <X className="h-6 w-6" />
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto py-8 px-4">
            <MenuProvider menu_id="16" className="w-full">
              <MobileNavConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="mt-8 px-4">
              <Link to="/ContactUsPage" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full rounded-[10px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(37,99,235,0.3)] font-semibold h-12 text-lg">
                  Request Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>}
    </header>;
}